import $ from 'jquery';
import * as THREE from 'three';
import { Viewer } from '../main';
import { roundDigit } from '../misc';
import LocalScene from '../projections';
import unitConverter from '../utilities/unit-converter';
import { GroundPlaneMeasurement } from './image-only';
import { AerialAreaMeasurement, AerialLengthMeasurement, Measurement3D, MeasurementArea} from './measurement';
import MinimapMeasure from './measurement-aerial-view';
import MeasurementConstructor from './measurement-constructor';
import MeasurementFormatter from './measurement-formatter';
import MeasurementGroup from './measurement-group';
import MeasurementGroupExporter from './measurement-group-exporter';
import MeasurementSceneView from './measurement-scene-view';

export default class MeasurementController {
  public viewer: Viewer;
  public enabled = false;
  private imageOnlyType = "plane";
  public measureType = "standard";
  public movingEndpoint = false;

  public formatter: MeasurementFormatter;
  public measureSceneView: MeasurementSceneView;
  public measurementAerialView: MinimapMeasure;
  public measurementBuilder: MeasurementConstructor;
  public measurementGroupExporter: MeasurementGroupExporter;
  public measurementGroup: MeasurementGroup;
  public setViewerTooltipText;
  public setAerialTooltipText;
  public setMeasurements;
  public scene: THREE.Scene;

  constructor(viewer: Viewer, props) {
    const {setViewerTooltipText, setAerialTooltipText, setMeasurements} = props;

    this.viewer = viewer;
    this.setViewerTooltipText = setViewerTooltipText;
    this.setAerialTooltipText = setAerialTooltipText;
    this.setMeasurements = setMeasurements;

    this.initScene();

    this.formatter = new MeasurementFormatter("m");
    this.measureSceneView = new MeasurementSceneView(this, this.scene);
    this.measurementAerialView = new MinimapMeasure(this);
    this.measurementBuilder = new MeasurementConstructor(this, this.pointclouds);
    this.measurementGroupExporter = new MeasurementGroupExporter(this);
    this.measurementGroup = new MeasurementGroup(this);
  }

  get pointclouds() {
    return this.viewer.pointclouds;
  }

  get controls() {
    return this.viewer.controls;
  }

  get isImageOnly() {
    return this.viewer.isImageOnly;
  }

  get measuring() {
    return this.measurementBuilder.activeMeasurement;
  }

  get drawing() {
    return this.measurementBuilder.activeMeasurement;
  }

  get notEmpty() {
    return this.measurementGroup.measurements.length > 0;
  }

  get saved() {
    return Boolean(this.measurementGroup.databaseID);
  }

  get draggable() {
    return this.measureSceneView.draggable;
  }

  get editable() {
    // Always true since view-only mode doesnt exist
    return true;
  }

  get measureExportType() {
    return this.measurementGroupExporter.dataExportFilter;
  }

  get hoveredPoint() {
    return this.measureSceneView.hoveredPoint;
  }

  enable() {
    this.enabled = true;
    this.createNewMeasurementGroup();
    this.measurementAerialView.enabled = true;
    this.measurementAerialView.init();
  }

  clear() {
    this.measureSceneView.removeAll();
    this.measurementAerialView.clear();
  }

  disable() {
    this.enabled = false;
    this.stopDrawingItems();
    this.measurementGroup.reset();
    this.measurementAerialView.enabled = false;
    this.measurementAerialView.removeInteraction();
    this.measurementAerialView.hideTooltip();
    this.measureSceneView.hideTooltip();

    this.updateAreaShaders();
  }

  reset() {
    this.clear();
    this.disable();
  }

  initScene() {
    const scene = new THREE.Scene();
    const light = new THREE.AmbientLight(0xFFFFFF);
    scene.add(light);
    this.scene = scene;
  }

  loadMeasurementData(data) {
    try {
      const projection = data.projection;
      const sameProjection = LocalScene.currentProjection(projection.name);

      if (sameProjection === false) {
        return {
          success: false,
          message: `Projection mismatch. Projection used was '${projection.name}'`
        };
      };

      // Clear data and create new group
      this.createNewMeasurementGroup();

      // Load measurements into new group
      let measurements = data.measurements;
      this.measurementGroup.loadMeasurements(measurements);
      this.refreshMeasurementList();

      return {
        success: true,
        message: `Loaded ${data.count} measurements`
      };
    } catch {
      this.createNewMeasurementGroup();

      return {
        success: false,
        message: "Error loading measurement group"
      };
    }
  }

  updateMaxMeasureDistance(units) {
    let $maxMeasureDistance = $("#max-measure-distance");

    if (!this.isImageOnly) {
      $maxMeasureDistance.text('');
      return;
    }

    let distance = new GroundPlaneMeasurement(this, this.viewer).maxDistance2D;
    let maxMeasureDistance = unitConverter.convert(distance, 'm', units);
    maxMeasureDistance = roundDigit(maxMeasureDistance, 2).toFixed(2);
    let message = `Maximum Measure Distance from Position: ${maxMeasureDistance} ${units}`;

    $maxMeasureDistance.text(message);
  }

  cancelMeasurement(event) {
    this.stopDrawingItems();
    this.checkForGrabbingPoint(event);
  }

  returnCoordinate(obj) {
    return false;
    /*
    return isLineworkObject(obj)
      || isFeatureServerObject(obj)
      || obj instanceof TexturedSprite;
    */
  }

  getPlanePosition(event) {
    let groundPlane = new GroundPlaneMeasurement(this, this.viewer);
    let coordinate = groundPlane.getPosition(event);
    return coordinate;
  }

  getCoordinate(event) {
    let coordinate = null;

    if (this.isImageOnly) {
      if (this.imageOnlyType === "plane") {
        coordinate = this.getPlanePosition(event);
      }
    } else {
      // Check for collection data (esri features)
      coordinate = this.controls.getPickerResults(event);
    }

    return coordinate;
  }

  mouseClick(event) {
    if (!this.editable) {
      return;
    }

    // Send position to measurements
    let coordinate = this.hoveredPoint
      ? this.hoveredPoint.position
      : this.getCoordinate(event);

    if (!coordinate) {
      return;
    }

    let result = this.measurementBuilder.add(coordinate);

    if (!result.valid) {
      this.cancelMeasurement(event);
      return;
    }

    if (result.added) {
      this.measureSceneView.addTemporaryPoint(coordinate);
      this.updateMeasureText();
    }

    if (this.measurementBuilder.isFinished) {
      this.finishMeasurement(event);
    }
  }

  onMouseMove(event, controls, mouse = null) {
    const mouseMoved = mouse ? mouse.movement : false;
    const mouseDown = mouse ? mouse.down : false;
    const isLeftClick = mouse ? mouse.button === 0 : false;

    this.movingEndpoint = false;

    if (!mouseMoved && !mouseDown) {
      this.measureSceneView.checkEndpointSelected(event);
      let draggable = this.draggable;
      controls.restrictOrbitMovement(!draggable);
    }

    if (mouse === null) {
      return;
    }

    if (this.enabled) {
      if (this.measuring) {
        let coordinate = this.getCoordinate(event);
        this.measureSceneView.addActiveEndpoint(coordinate);
      }

      if (this.draggable) {
        this.setPointGrabTooltip();
        if (mouseDown) {
          if (isLeftClick) {
            let coordinate = this.getCoordinate(event);
            this.measureSceneView.updateMeasureEndpoint(coordinate);
          }
          this.movingEndpoint = true;
          return;
        }
      } else {
        this.updateMeasureText();
      }
    }

    return false;
  }

  resetDragState() {
    this.measureSceneView.setDragState(false, null);
  }

  finishMeasurement(event) {
    this.updateMeasureText();
    let measurement = this.measurementBuilder.getMeasurement();

    if (measurement instanceof MeasurementArea) {
      measurement.generateSurface();
    }

    this.measureSceneView.removeTemporaryObjects();
    this.measurementGroup.add(measurement);
    this.renderMeasurement(measurement);

    // Check if we are above a point (from any measurement) after finishing
    this.checkForGrabbingPoint(event);
  }

  checkForGrabbingPoint(event) {
    // Small timeout is needed for whatever reason
    let timeoutMilliseconds = 20;

    setTimeout(() => {
      this.onMouseMove(event, this.controls);
      if (this.draggable) {
        this.setPointGrabTooltip();
      }
    }, timeoutMilliseconds);
  }

  stopDrawingItems() {
    this.measurementBuilder.reset();
    this.measureSceneView.removeTemporaryObjects();

    if (this.measurementAerialView.sketch !== null) {
      this.measurementAerialView.clear();
      this.redrawAerialGeometry();
    }

    this.updateMeasureText(false);
  }

  setPointGrabTooltip() {
    this.measureSceneView.setPointGrabTooltip();
  }

  measureGrabFinished() {
    this.measureSceneView.measureGrabFinished();
  }

  updateAreaShaders() {
    let areaGeometry = this.measurementGroup.getAreaGeometry();
    let pointcloud = this.viewer.pointclouds;
    pointcloud.updateAreaVertices(areaGeometry);
  }

  refreshMeasurementList() {
    this.setMeasurements([...this.measurementGroup.measurements]);
  }

  updateMeasureText(checkMorePoints = true) {
    let morepoints = false;
    let measurement = this.measurementBuilder.measurement;

    if (checkMorePoints) {
      morepoints = !this.measurementBuilder.isFinished;
      if (measurement.vertices.length === 0) {
        morepoints = false;
      }
    }

    this.measureSceneView.setTooltipText(measurement, morepoints);
  }

  addAerialMeasurement(geometry) {
    let measurement;

    if (this.measurementAerialView.measurementType === 'LineString') {
      measurement = new AerialLengthMeasurement(geometry);
    } else {
      measurement = new AerialAreaMeasurement(geometry);
    }

    measurement.setController(this);
    this.measurementGroup.add(measurement);
  }

  createNewMeasurementGroup() {
    this.measurementAerialView.clear();
    this.measureSceneView.removeAll();
    this.measurementGroup = new MeasurementGroup(this);
    this.setMeasurementType(this.measureType);
    this.updateAreaShaders();
  }

  setMeasurementType(measurementType) {
    if (!this.enabled) {
      return;
    }

    this.measureType = measurementType;
    this.measurementBuilder.setBuilder(measurementType);
    this.measureSceneView.removeTemporaryObjects();

    let measurement = this.measurementBuilder.measurement;
    this.measureSceneView.setTooltipText(measurement);

    this.stopDrawingItems();
    this.measurementAerialView.setMeasurementType(measurementType);
  }

  removeByID(id) {
    this.measureSceneView.removeByID(id);
    this.measurementGroup.removeByID(id);

    this.measurementAerialView.clear();
    this.redrawAerialGeometry();

    // Update area measurement shader
    this.updateAreaShaders();
  }

  setTitle(id, title) {
    const measurements = this.measurementGroup.measurements;
    const measurement = measurements.find(measurement => measurement.id === id);
    if (measurement) {
      measurement.title = title;
      this.refreshMeasurementList();
    }
  }

  redrawAerialGeometry(measurement = null, hover = false) {
    if (measurement) {
      this.measurementGroup.getMeasurements2D().forEach(measurement_ => {
        if (measurement_.id === measurement.id) {
          this.measurementAerialView.loadGeometry(measurement_, hover);
        } else {
          this.measurementAerialView.loadGeometry(measurement_);
        }
      });
    } else {
      this.measurementGroup.getMeasurements2D().forEach(measurement => {
        this.measurementAerialView.loadGeometry(measurement);
      });
    }
  }

  clearByID(id) {
    this.measureSceneView.removeByID(id);
  }

  removeAll() {
    this.measurementGroup.removeAll();
    this.clearViews();
  }

  clearViews() {
    this.measureSceneView.removeAll();
    this.measurementAerialView.clear();
  }

  export(type, ordering) {
    if (type === "csv") {
      this.exportCSV(ordering);
    } else if (type === "dat") {
      this.exportDAT();
    }
  }

  exportDAT() {
    this.measurementGroupExporter
      .exportDAT(this.measurementGroup);
  }

  exportCSV(ordering) {
    this.measurementGroupExporter.exportCSV(
      this.measurementGroup, ordering);
  }

  /* Not implemented */
  exportLinework(type, projection, version) {
    this.measurementGroupExporter
      .exportLinework(this.measurementGroup, type, version);
  }

  setUnits(units) {
    this.formatter.setUnits(units);
    this.updateMaxMeasureDistance(units);

    if (this.measurementGroup) {
      this.reload();
    }
  }

  reload() {
    this.clearViews();
    this.measurementGroup.measurements.forEach(measurement => {
      this.renderMeasurement(measurement);
    });
    this.refreshMeasurementList();
  }

  updateRenderColor(measurement, hover = false) {
    if (measurement instanceof Measurement3D) {
      this.measureSceneView.updateMaterial(measurement.id, hover);
    } else {
      this.measurementAerialView.clear();
      this.redrawAerialGeometry(measurement, hover);
    }
  }

  renderMeasurement(measurement) {
    if (measurement instanceof Measurement3D) {
      this.measureSceneView.draw(measurement);
    } else {
      this.measurementAerialView.loadGeometry(measurement);
    }
  }

  update(changeDetector) {
    this.measureSceneView.update(changeDetector);
  }
}
